import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Logo } from '@resolution-processing/balance';
import {
  adminListCompanies,
  adminCreateCompany,
  adminDeleteCompany,
  adminAddDomain,
  adminRemoveDomain,
  adminAddEmail,
  adminRemoveEmail,
  type AdminCompany,
} from '../api.ts';
import styles from './AdminPage.module.css';

// ── Per-company add-field forms ───────────────────────────────────────────────

interface AddEntryFormProps {
  label: string;
  placeholder: string;
  submitLabel: string;
  onAdd: (value: string) => Promise<string | undefined>;
}

function AddEntryForm({ label, placeholder, submitLabel, onAdd }: AddEntryFormProps) {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(undefined);
    const err = await onAdd(trimmed);
    setLoading(false);
    if (err) {
      setError(err);
    } else {
      setValue('');
    }
  };

  return (
    <form className={styles.addForm} onSubmit={handleSubmit} noValidate>
      <div className={styles.addFormInput}>
        <Input
          type="text"
          name={label}
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={setValue}
          disabled={loading}
          error={!!error}
          helperText={error}
        />
      </div>
      <Button
        variant="primary"
        type="submit"
        className={styles.actionButton}
        disabled={loading || !value.trim()}
      >
        {submitLabel}
      </Button>
    </form>
  );
}

// ── Company card ──────────────────────────────────────────────────────────────

interface CompanyCardProps {
  company: AdminCompany;
  onUpdate: (updated: AdminCompany) => void;
  onDelete: (companyId: string) => void;
}

function CompanyCard({ company, onUpdate, onDelete }: CompanyCardProps) {
  const { t } = useTranslation('ui');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
  const [removingDomain, setRemovingDomain] = useState<string | null>(null);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [domainError, setDomainError] = useState<string | undefined>(undefined);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const handleDelete = async () => {
    if (!window.confirm(t('admin.confirmDelete', { name: company.name }))) return;
    setDeleteLoading(true);
    setDeleteError(undefined);
    const result = await adminDeleteCompany(company.companyId);
    setDeleteLoading(false);
    if (!result.ok) {
      setDeleteError(result.error);
    } else {
      onDelete(company.companyId);
    }
  };

  const handleAddDomain = async (domain: string): Promise<string | undefined> => {
    setDomainError(undefined);
    const result = await adminAddDomain(company.companyId, domain);
    if (!result.ok) {
      setDomainError(result.error);
      return result.error;
    }
    onUpdate(result.company);
    return undefined;
  };

  const handleRemoveDomain = async (domain: string) => {
    setRemovingDomain(domain);
    setDomainError(undefined);
    const result = await adminRemoveDomain(company.companyId, domain);
    setRemovingDomain(null);
    if (!result.ok) {
      setDomainError(result.error);
    } else {
      onUpdate(result.company);
    }
  };

  const handleAddEmail = async (email: string): Promise<string | undefined> => {
    setEmailError(undefined);
    const result = await adminAddEmail(company.companyId, email);
    if (!result.ok) {
      setEmailError(result.error);
      return result.error;
    }
    onUpdate(result.company);
    return undefined;
  };

  const handleRemoveEmail = async (email: string) => {
    setRemovingEmail(email);
    setEmailError(undefined);
    const result = await adminRemoveEmail(company.companyId, email);
    setRemovingEmail(null);
    if (!result.ok) {
      setEmailError(result.error);
    } else {
      onUpdate(result.company);
    }
  };

  return (
    <div className={styles.companyCard}>
      <div className={styles.companyHeader}>
        <h2 className={styles.companyName}>{company.name}</h2>
        <button
          type="button"
          className={styles.deleteButton}
          disabled={deleteLoading}
          onClick={handleDelete}
        >
          {t('admin.deleteCompany')}
        </button>
      </div>

      {deleteError && (
        <p className={styles.fieldError}>{t('admin.actionError', { error: deleteError })}</p>
      )}

      {/* Allowed domains */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>{t('admin.domains')}</div>
        <div className={styles.tagList}>
          {company.domains.length === 0 && (
            <span className={styles.emptyTag}>{t('admin.noDomains')}</span>
          )}
          {company.domains.map((domain) => (
            <span key={domain} className={styles.tag}>
              {domain}
              <button
                type="button"
                className={styles.tagRemoveButton}
                aria-label={`${t('admin.remove')} ${domain}`}
                disabled={removingDomain === domain}
                onClick={() => handleRemoveDomain(domain)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        {domainError && (
          <p className={styles.fieldError}>{t('admin.actionError', { error: domainError })}</p>
        )}
        <AddEntryForm
          label={t('admin.addDomainLabel')}
          placeholder={t('admin.addDomainPlaceholder')}
          submitLabel={t('admin.addDomain')}
          onAdd={handleAddDomain}
        />
      </div>

      {/* Allowed specific emails */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>{t('admin.emails')}</div>
        <div className={styles.tagList}>
          {company.emails.length === 0 && (
            <span className={styles.emptyTag}>{t('admin.noEmails')}</span>
          )}
          {company.emails.map((email) => (
            <span key={email} className={styles.tag}>
              {email}
              <button
                type="button"
                className={styles.tagRemoveButton}
                aria-label={`${t('admin.remove')} ${email}`}
                disabled={removingEmail === email}
                onClick={() => handleRemoveEmail(email)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        {emailError && (
          <p className={styles.fieldError}>{t('admin.actionError', { error: emailError })}</p>
        )}
        <AddEntryForm
          label={t('admin.addEmailLabel')}
          placeholder={t('admin.addEmailPlaceholder')}
          submitLabel={t('admin.addEmail')}
          onAdd={handleAddEmail}
        />
      </div>
    </div>
  );
}

// ── Admin page root ───────────────────────────────────────────────────────────

type PageState =
  | { status: 'loading' }
  | { status: 'not-authorized' }
  | { status: 'error'; message: string }
  | { status: 'ready'; companies: AdminCompany[] };

export function AdminPage() {
  const { t } = useTranslation('ui');
  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });

  // Create-company form
  const [newName, setNewName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | undefined>(undefined);

  // On mount: call GET /admin/companies — server is authoritative.
  useEffect(() => {
    adminListCompanies().then((result) => {
      if (!result.ok) {
        if (result.status === 401 || result.status === 403) {
          setPageState({ status: 'not-authorized' });
        } else {
          setPageState({ status: 'error', message: result.error });
        }
      } else {
        setPageState({ status: 'ready', companies: result.companies });
      }
    });
  }, []);

  const handleCompanyUpdate = (updated: AdminCompany) => {
    setPageState((prev) => {
      if (prev.status !== 'ready') return prev;
      return {
        ...prev,
        companies: prev.companies.map((c) =>
          c.companyId === updated.companyId ? updated : c
        ),
      };
    });
  };

  const handleCompanyDelete = (companyId: string) => {
    setPageState((prev) => {
      if (prev.status !== 'ready') return prev;
      return {
        ...prev,
        companies: prev.companies.filter((c) => c.companyId !== companyId),
      };
    });
  };

  const handleCreateCompany = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreateLoading(true);
    setCreateError(undefined);
    const result = await adminCreateCompany(trimmed);
    setCreateLoading(false);
    if (!result.ok) {
      setCreateError(result.error);
    } else {
      setNewName('');
      setPageState((prev) => {
        if (prev.status !== 'ready') return prev;
        return { ...prev, companies: [...prev.companies, result.company] };
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <header className={styles.topBar}>
        <a href="/" className={styles.logoLink}><Logo color="white" height={32} /></a>
      </header>
      <div className={styles.container}>
        <div className={styles.inner}>

        {pageState.status === 'loading' && (
          <p className={styles.loading}>{t('admin.loading')}</p>
        )}

        {pageState.status === 'not-authorized' && (
          <p className={styles.notAuthorized}>{t('admin.notAuthorized')}</p>
        )}

        {pageState.status === 'error' && (
          <p className={styles.loadError}>{t('admin.loadError')} {pageState.message}</p>
        )}

        {pageState.status === 'ready' && (
          <>
            <h1 className={styles.pageTitle}>{t('admin.title')}</h1>
            {/* Create company */}
            <form className={styles.createForm} onSubmit={handleCreateCompany} noValidate>
              <div className={styles.createFormInput}>
                <Input
                  type="text"
                  name="newCompanyName"
                  label={t('admin.newCompanyLabel')}
                  placeholder={t('admin.newCompanyPlaceholder')}
                  value={newName}
                  onChange={setNewName}
                  disabled={createLoading}
                  error={!!createError}
                  helperText={createError}
                />
              </div>
              <Button
                variant="primary"
                type="submit"
                className={styles.actionButton}
                disabled={createLoading || !newName.trim()}
              >
                {createLoading ? t('admin.addCompanyLoading') : t('admin.addCompany')}
              </Button>
            </form>

            {pageState.companies.length === 0 && (
              <p className={styles.emptyState}>{t('admin.noCompanies')}</p>
            )}

            {pageState.companies.map((company) => (
              <CompanyCard
                key={company.companyId}
                company={company}
                onUpdate={handleCompanyUpdate}
                onDelete={handleCompanyDelete}
              />
            ))}
          </>
        )}
        </div>
      </div>
    </div>
  );
}
